import { useEffect, useState } from 'react';

import { InvoiceDetailsDetailTab } from './invoice-details-detail-tab';
import { InvoiceDetailsEmptySelection } from './invoice-details-empty-selection';
import { InvoiceDetailsErrorState } from './invoice-details-error-state';
import { InvoiceDetailsGeneralTab } from './invoice-details-general-tab';
import { InvoiceDetailsLoading } from './invoice-details-loading';
import { InvoiceDetailsNotesTab } from './invoice-details-notes-tab';
import { InvoiceDetailsTabBar } from './invoice-details-tab-bar';
import type { DetailsTab, InvoiceDetailsPanelProps } from './types';
import { INVOICE_DETAILS_TABS } from './types';
import { useInvoiceDetailsDerived } from './use-invoice-details-derived';

export type { InvoiceDetailsPanelProps } from './types';

function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
}

/** macOS: ⌥+number sets `e.key` to symbols (¡, ™, £, …), not "1".."3" — use physical `code` instead. */
const DIGIT_TO_TAB_INDEX: Record<string, number> = {
  Digit1: 0,
  Digit2: 1,
  Digit3: 2,
};

export function InvoiceDetailsPanel({ hasSelection, selectedInvoice, isLoading, isError }: InvoiceDetailsPanelProps) {
  const [activeTab, setActiveTab] = useState<DetailsTab>('general');

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!hasSelection) return;
      if (isTypingTarget(e.target)) return;

      const idx = DIGIT_TO_TAB_INDEX[e.code];
      if (idx === undefined) return;

      const goToTab = () => setActiveTab(INVOICE_DETAILS_TABS[idx].key);

      // ⌘/Ctrl+1–3: same capture override pattern as invoice PDF ⌘D (blocks sidebar ⌘number).
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
        if (e.repeat) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        goToTab();
        return;
      }

      // ⌥+1–3 (physical digit codes; no ⌘/Ctrl/Shift).
      if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      e.stopPropagation();
      goToTab();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [hasSelection]);

  const derived = useInvoiceDetailsDerived(selectedInvoice);

  if (!hasSelection) {
    return <InvoiceDetailsEmptySelection />;
  }

  const tabBody = (() => {
    if (isLoading) {
      return <InvoiceDetailsLoading />;
    }
    if (isError || !selectedInvoice) {
      return <InvoiceDetailsErrorState />;
    }
    if (activeTab === 'general') {
      return (
        <InvoiceDetailsGeneralTab
          invoice={selectedInvoice}
          agreementLinks={derived.agreementLinks}
          terms={derived.terms}
          purchaseOrders={derived.purchaseOrders}
        />
      );
    }
    if (activeTab === 'detail') {
      return <InvoiceDetailsDetailTab invoice={selectedInvoice} billingAddressBlocks={derived.billingAddressBlocks} />;
    }
    return <InvoiceDetailsNotesTab invoice={selectedInvoice} />;
  })();

  return (
    <div className="border-c-border flex h-full flex-col overflow-hidden rounded-t-none rounded-b-xl border">
      <div className="min-h-0 flex-1 overflow-auto">{tabBody}</div>
      <InvoiceDetailsTabBar activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
}
